import { CountryCodeSelect } from "@components/shared/CountryCodeSelect";
import { type SearchLocation, SearchLocationSelect } from "@components/shared/SearchLocationSelect";
import Image from "@tiptap/extension-image";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Heading1,
  Heading2,
  ImageIcon,
  Italic,
  List,
  ListOrdered,
  Minus,
  Plus,
  Redo,
  Undo,
} from "lucide-react";
import { useCallback, useState } from "react";

export interface SalaryFields {
  salaryBase: string;
  salaryBonus: string;
  salaryEquity: string;
  salaryBenefits: string;
  salaryCurrency: string;
  targetStartDate: string;
  assessmentProcess: string;
  backgroundCheckRequirements: string;
}

export interface RoleProfileFields {
  roleTitle: string;
  roleMandate: string;
  contextNotes: string;
  successOutcomes: string[];
  culturalFitNotes: string;
  motivationFitNotes: string;
  searchCountryCodes: string[];
  searchLocations: SearchLocation[];
}

interface BriefEditorProps {
  additionalNotes: string;
  initialSalary?: Partial<SalaryFields>;
  initialRoleProfile?: Partial<RoleProfileFields>;
  onSave: (
    additionalNotes: string,
    changeSummary: string | undefined,
    salary: SalaryFields,
    roleProfile: RoleProfileFields,
  ) => void;
  onCancel: () => void;
  saving?: boolean;
  requireChangeSummary?: boolean;
}

async function uploadImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);

  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
  const response = await fetch("/uploads", {
    method: "POST",
    headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
    body: formData,
  });

  const data = await response.json();
  return data.url;
}

const CURRENCIES = ["GBP", "USD", "EUR", "CHF", "SGD", "AUD", "CAD"];

export function BriefEditor({
  additionalNotes,
  initialSalary,
  initialRoleProfile,
  onSave,
  onCancel,
  saving,
  requireChangeSummary,
}: BriefEditorProps) {
  const [changeSummary, setChangeSummary] = useState("");
  const [salaryBase, setSalaryBase] = useState(initialSalary?.salaryBase ?? "");
  const [salaryBonus, setSalaryBonus] = useState(initialSalary?.salaryBonus ?? "");
  const [salaryEquity, setSalaryEquity] = useState(initialSalary?.salaryEquity ?? "");
  const [salaryBenefits, setSalaryBenefits] = useState(initialSalary?.salaryBenefits ?? "");
  const [salaryCurrency, setSalaryCurrency] = useState(initialSalary?.salaryCurrency ?? "GBP");
  const [targetStartDate, setTargetStartDate] = useState(initialSalary?.targetStartDate ?? "");
  const [assessmentProcess, setAssessmentProcess] = useState(
    initialSalary?.assessmentProcess ?? "",
  );
  const [backgroundCheckRequirements, setBackgroundCheckRequirements] = useState(
    initialSalary?.backgroundCheckRequirements ?? "",
  );

  const initialOutcomes = initialRoleProfile?.successOutcomes;
  const [roleTitle, setRoleTitle] = useState(initialRoleProfile?.roleTitle ?? "");
  const [roleMandate, setRoleMandate] = useState(initialRoleProfile?.roleMandate ?? "");
  const [contextNotes, setContextNotes] = useState(initialRoleProfile?.contextNotes ?? "");
  const [successOutcomes, setSuccessOutcomes] = useState<string[]>(
    initialOutcomes && initialOutcomes.length > 0 ? initialOutcomes : [""],
  );
  const [culturalFitNotes, setCulturalFitNotes] = useState(
    initialRoleProfile?.culturalFitNotes ?? "",
  );
  const [motivationFitNotes, setMotivationFitNotes] = useState(
    initialRoleProfile?.motivationFitNotes ?? "",
  );
  const [searchCountryCodes, setSearchCountryCodes] = useState<string[]>(
    initialRoleProfile?.searchCountryCodes ?? [],
  );
  const [searchLocations, setSearchLocations] = useState<SearchLocation[]>(
    initialRoleProfile?.searchLocations ?? [],
  );

  const addOutcome = () => setSuccessOutcomes([...successOutcomes, ""]);
  const removeOutcome = (i: number) =>
    setSuccessOutcomes(successOutcomes.filter((_, j) => j !== i));
  const updateOutcome = (i: number, v: string) =>
    setSuccessOutcomes(successOutcomes.map((o, j) => (j === i ? v : o)));

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        inline: true,
        HTMLAttributes: { class: "max-w-full rounded" },
      }),
    ],
    content: additionalNotes,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm dark:prose-invert max-w-none min-h-[200px] p-4 focus:outline-none text-text-primary",
      },
      handleDrop(view, event) {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;

        const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
        if (!imageFiles.length) return false;

        event.preventDefault();
        for (const file of imageFiles) {
          uploadImage(file).then((url) => {
            const { schema } = view.state;
            const node = schema.nodes.image.create({ src: url });
            const transaction = view.state.tr.replaceSelectionWith(node);
            view.dispatch(transaction);
          });
        }
        return true;
      },
      handlePaste(view, event) {
        const files = event.clipboardData?.files;
        if (!files?.length) return false;

        const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
        if (!imageFiles.length) return false;

        event.preventDefault();
        for (const file of imageFiles) {
          uploadImage(file).then((url) => {
            const { schema } = view.state;
            const node = schema.nodes.image.create({ src: url });
            const transaction = view.state.tr.replaceSelectionWith(node);
            view.dispatch(transaction);
          });
        }
        return true;
      },
    },
  });

  const addImage = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file || !editor) return;
      const url = await uploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
    };
    input.click();
  }, [editor]);

  const handleSave = () => {
    if (!editor) return;
    onSave(
      editor.getHTML(),
      changeSummary || undefined,
      {
        salaryBase,
        salaryBonus,
        salaryEquity,
        salaryBenefits,
        salaryCurrency,
        targetStartDate,
        assessmentProcess,
        backgroundCheckRequirements,
      },
      {
        roleTitle,
        roleMandate,
        contextNotes,
        successOutcomes: successOutcomes.filter((o) => o.trim() !== ""),
        culturalFitNotes,
        motivationFitNotes,
        searchCountryCodes,
        searchLocations,
      },
    );
  };

  if (!editor) return null;

  return (
    <div className="rounded-lg bg-card shadow-sm ring-1 ring-ring">
      {/* Role Profile — fields the role blueprint generator reads. */}
      <div>
        <div className="px-4 py-2 text-xs font-medium text-text-secondary">Role Profile</div>
        <div className="space-y-3 px-4 pb-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">Role Title</label>
            <input
              type="text"
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              placeholder="e.g. Chief Technology Officer"
              className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">Role Mandate</label>
            <textarea
              value={roleMandate}
              onChange={(e) => setRoleMandate(e.target.value)}
              rows={3}
              placeholder="What is this role mandated to achieve?"
              className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">Context Notes</label>
            <textarea
              value={contextNotes}
              onChange={(e) => setContextNotes(e.target.value)}
              rows={2}
              placeholder="Organisational context, situational factors..."
              className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-text-secondary">
                Success Outcomes
              </label>
              <button
                type="button"
                onClick={addOutcome}
                className="flex items-center gap-1 text-xs text-accent hover:text-accent/80"
              >
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="mt-1 space-y-2">
              {successOutcomes.map((outcome, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: dynamic list without stable IDs
                <div key={`outcome-${i}`} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={outcome}
                    onChange={(e) => updateOutcome(i, e.target.value)}
                    className="flex-1 rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                    placeholder={`Outcome ${i + 1}`}
                  />
                  {successOutcomes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeOutcome(i)}
                      className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                    >
                      <Minus size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">Cultural Fit</label>
            <textarea
              value={culturalFitNotes}
              onChange={(e) => setCulturalFitNotes(e.target.value)}
              rows={2}
              placeholder="Values, working style, team dynamics..."
              className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">Motivation Fit</label>
            <textarea
              value={motivationFitNotes}
              onChange={(e) => setMotivationFitNotes(e.target.value)}
              rows={2}
              placeholder="Career drivers, what makes this role attractive..."
              className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <CountryCodeSelect
            value={searchCountryCodes}
            onChange={setSearchCountryCodes}
            label="Search Location"
            hint="Countries to geo-target X-Ray searches from. Overrides client/organisation defaults."
          />
          <SearchLocationSelect
            value={searchLocations}
            onChange={setSearchLocations}
            label="Narrower Locations"
            hint="Cities or regions within those countries, if the role isn't country-wide."
          />
        </div>
      </div>

      {/* Compensation & Start Date */}
      <div className="border-t border-border">
        <div className="px-4 py-2 text-xs font-medium text-text-secondary">
          Compensation &amp; Start Date
        </div>
        <div className="space-y-3 px-4 pb-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary">Base Salary</label>
              <input
                type="number"
                value={salaryBase}
                onChange={(e) => setSalaryBase(e.target.value)}
                placeholder="e.g. 150000"
                className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary">Currency</label>
              <select
                value={salaryCurrency}
                onChange={(e) => setSalaryCurrency(e.target.value)}
                className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary">Bonus Details</label>
              <input
                type="text"
                value={salaryBonus}
                onChange={(e) => setSalaryBonus(e.target.value)}
                placeholder="e.g. 20% annual bonus"
                className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary">
                Equity Details
              </label>
              <input
                type="text"
                value={salaryEquity}
                onChange={(e) => setSalaryEquity(e.target.value)}
                placeholder="e.g. 0.5% vesting over 4 years"
                className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary">Benefits</label>
              <input
                type="text"
                value={salaryBenefits}
                onChange={(e) => setSalaryBenefits(e.target.value)}
                placeholder="e.g. Private health, pension, car allowance"
                className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary">
                Target Start Date
              </label>
              <input
                type="date"
                value={targetStartDate}
                onChange={(e) => setTargetStartDate(e.target.value)}
                className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Client Process */}
      <div className="border-t border-border">
        <div className="px-4 py-2 text-xs font-medium text-text-secondary">
          Client Assessment &amp; Background Checks
        </div>
        <div className="space-y-3 px-4 pb-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              Assessment Process
            </label>
            <textarea
              value={assessmentProcess}
              onChange={(e) => setAssessmentProcess(e.target.value)}
              placeholder="Describe interview stages, panels, decision-making process..."
              rows={3}
              className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              Background Check Requirements
            </label>
            <textarea
              value={backgroundCheckRequirements}
              onChange={(e) => setBackgroundCheckRequirements(e.target.value)}
              placeholder="DBS checks, referencing, right-to-work verification..."
              rows={3}
              className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
        </div>
      </div>

      {/* Additional notes — supplementary brief context for downstream generators. */}
      <div className="border-t border-border">
        <div className="px-4 py-2 text-xs font-medium text-text-secondary">Additional notes</div>
        <p className="px-4 pb-2 text-xs text-text-faint">
          Anything that doesn&apos;t fit the structured fields above. The blueprint, personas,
          market map, and assessment generators all read this as supplementary context.
        </p>
        <div className="flex flex-wrap items-center gap-1 border-y border-border p-2">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            title="Bold"
          >
            <Bold size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
            title="Italic"
          >
            <Italic size={16} />
          </ToolbarButton>
          <div className="mx-1 h-5 w-px bg-border" />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            active={editor.isActive("heading", { level: 1 })}
            title="Heading 1"
          >
            <Heading1 size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive("heading", { level: 2 })}
            title="Heading 2"
          >
            <Heading2 size={16} />
          </ToolbarButton>
          <div className="mx-1 h-5 w-px bg-border" />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            title="Bullet list"
          >
            <List size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive("orderedList")}
            title="Ordered list"
          >
            <ListOrdered size={16} />
          </ToolbarButton>
          <div className="mx-1 h-5 w-px bg-border" />
          <ToolbarButton onClick={addImage} title="Insert image">
            <ImageIcon size={16} />
          </ToolbarButton>
          <div className="mx-1 h-5 w-px bg-border" />
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
          >
            <Undo size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
          >
            <Redo size={16} />
          </ToolbarButton>
        </div>
        <EditorContent editor={editor} />
      </div>

      {/* Footer */}
      <div className="flex items-center gap-3 border-t border-border p-3">
        <input
          type="text"
          value={changeSummary}
          onChange={(e) => setChangeSummary(e.target.value)}
          placeholder={
            requireChangeSummary ? "What changed? (required)" : "What changed? (optional)"
          }
          className="flex-1 rounded border border-input-border bg-card px-3 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || (requireChangeSummary && !changeSummary.trim())}
          className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

function ToolbarButton({
  children,
  onClick,
  active,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`rounded p-1.5 ${
        active ? "bg-accent/10 text-accent" : "text-text-secondary hover:bg-card-hover"
      } disabled:opacity-30`}
    >
      {children}
    </button>
  );
}
